import React, { useEffect, useState } from 'react';
import './Lightbox.scss';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard } from 'swiper/modules';
import type { Swiper as SwiperClass } from 'swiper';
import 'swiper/css';

type LightboxProps = {
  images: string[];
  /** optional alt text per image */
  alts?: string[];
  initialIndex?: number;
  onClose: () => void;
};

const Chevron = ({ dir }: { dir: 'left' | 'right' }) => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {dir === 'left' ? <path d="M10 3 5 8l5 5" /> : <path d="m6 3 5 5-5 5" />}
  </svg>
);

export function Lightbox({ images, alts, initialIndex = 0, onClose }: LightboxProps) {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    document.body.classList.add('no-scroll');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('no-scroll');
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label="Project gallery">
      <div className="lightbox__backdrop" onClick={onClose} />

      <div className="lightbox__top">
        <span className="lightbox__counter">
          <span>{String(index + 1).padStart(2, '0')}</span> / {String(images.length).padStart(2, '0')}
        </span>
        <button className="icon-btn icon-btn--dark lightbox__close" onClick={onClose} aria-label="Close gallery">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <path d="m3.5 3.5 9 9m0-9-9 9" />
          </svg>
        </button>
      </div>

      <Swiper
        className="lightbox__swiper"
        modules={[Keyboard]}
        keyboard={{ enabled: true }}
        spaceBetween={24}
        slidesPerView={1}
        initialSlide={initialIndex}
        onSwiper={setSwiper}
        onSlideChange={(s: SwiperClass) => setIndex(s.activeIndex)}
      >
        {images.map((src, i) => (
          <SwiperSlide key={src}>
            <div className="lightbox__slide" onClick={onClose}>
              <img
                src={src}
                alt={alts?.[i] ?? `Project ${i + 1}`}
                loading={Math.abs(i - index) < 3 ? 'eager' : 'lazy'}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="lightbox__nav">
        <button className="icon-btn icon-btn--dark" onClick={() => swiper?.slidePrev()} aria-label="Previous photo">
          <Chevron dir="left" />
        </button>
        <button className="icon-btn icon-btn--dark" onClick={() => swiper?.slideNext()} aria-label="Next photo">
          <Chevron dir="right" />
        </button>
      </div>
    </div>
  );
}
